import { useContext, useEffect, useState } from 'react';
import { Link } from 'wouter';

import { getStoragePreferences } from '../../api/storage';
import { fetchBillingState } from '../../api/billing';
import { getPendingOffer } from '../../lib/pendingOffer';
import { planInForce } from './OnboardingPage';
import { readWelcomeAnswers } from '../../lib/welcomeAnswers';
import { SessionContext } from '../../session';

const DISMISSED_KEY = 'auteur.welcome.resume-dismissed';

/**
 * F-10: a way back to Welcome after leaving it. Shown on Projects while the questionnaire is unanswered
 * or the storage defaults are unconfirmed; it can be hidden for the rest of the browser session.
 */
export function ResumeSetupBanner({ storage = getStoragePreferences, billing = fetchBillingState }: {
  storage?: () => Promise<{ defaults_confirmed_at: string | null }>;
  billing?: () => Promise<{ subscribed: boolean; status?: string; paid_service?: boolean; offer_id?: string }>;
} = {}) {
  const sessionState = useContext(SessionContext);
  const orgKey = sessionState?.session?.org_id || 'signed-in';
  const [show, setShow] = useState(false);

  useEffect(() => {
    let active = true;
    try {
      if (window.sessionStorage.getItem(DISMISSED_KEY) === '1') return () => { active = false; };
    } catch {
      // Without session storage the banner simply shows.
    }
    // Welcome's own rules: questionnaire answered, product decided (a plan in force, or an explicit
    // "later" with no plan pending from sign-up; a started checkout is not a decision), storage confirmed.
    const answers = readWelcomeAnswers(orgKey);
    Promise.allSettled([storage(), billing()]).then(([preferences, plan]) => {
      if (!active) return;
      const planValue = plan.status === 'fulfilled' ? plan.value : null;
      const inForce = planInForce(planValue);
      const pending = getPendingOffer();
      const pendingPurchase = pending !== null && (!inForce || Boolean(planValue?.offer_id && pending.id !== planValue.offer_id));
      const decided = answers !== null && !pendingPurchase && (inForce || answers.plan === 'later');
      const stored = preferences.status === 'fulfilled' && Boolean(preferences.value.defaults_confirmed_at);
      setShow(!decided || !stored);
    });
    return () => { active = false; };
  }, [orgKey, storage, billing]);

  if (!show) return null;
  return (
    <aside className="resume-setup-banner" aria-label="Setup">
      <p><strong>Finish setting up Auteur.</strong> Pick your product and connect only the tools you need.</p>
      <Link to="/welcome">Resume setup</Link>
      <button type="button" className="secondary" onClick={() => {
        try { window.sessionStorage.setItem(DISMISSED_KEY, '1'); } catch { /* best effort */ }
        setShow(false);
      }}>Hide for now</button>
    </aside>
  );
}
